import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Shield, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, ArrowRight, KeyRound, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = process.env.REACT_APP_BACKEND_URL;

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8, label: "Au moins 8 caracteres" },
  { test: (p) => /[A-Z]/.test(p), label: "Une majuscule" },
  { test: (p) => /[a-z]/.test(p), label: "Une minuscule" },
  { test: (p) => /[0-9]/.test(p), label: "Un chiffre" },
];

export default function ActivateAccountPage() {
  usePageTitle("Activer votre espace");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [step, setStep] = useState("loading"); // loading | form | success | expired | invalid | resend
  const [accountInfo, setAccountInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) { setStep("invalid"); return; }
    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/verify-activation-token?token=${token}`);
        setAccountInfo(data);
        setResendEmail(data.email || "");
        setStep("form");
      } catch (e) {
        const msg = e.response?.data?.detail || "";
        if (msg.includes("expire")) setStep("expired");
        else setStep("invalid");
        // Try to extract email for resend
        if (e.response?.data?.email) setResendEmail(e.response.data.email);
      }
    };
    verify();
  }, [token]);

  const allRulesPass = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = allRulesPass && passwordsMatch && !submitting;

  const handleActivate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/activate-account`, { token, password });
      setStep("success");
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de l'activation";
      if (msg.includes("expire")) setStep("expired");
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!resendEmail) return;
    try {
      await axios.post(`${API}/api/auth/resend-activation`, { email: resendEmail });
      setResendSent(true);
    } catch { setResendSent(true); } // Always show success to prevent enumeration
  };

  return (
    <div className="min-h-screen bg-[#060A13] flex items-center justify-center px-4 py-12" data-testid="activate-account-page">
      <div className="w-full max-w-md">
        {/* Logo - Event Center branded */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center">
              <span className="text-white font-black text-lg">G</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">GLOTECH</span>
            <span className="text-xs font-mono text-[#F59E0B] ml-0.5">EVENT CENTER</span>
          </Link>
        </div>

        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-2xl p-8 shadow-2xl">
          {/* LOADING */}
          {step === "loading" && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-10 w-10 text-[#F59E0B] animate-spin mb-4" />
              <p className="text-[#94A3B8]">Verification du lien d'activation...</p>
            </div>
          )}

          {/* FORM */}
          {step === "form" && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-[#F59E0B]/10 rounded-xl">
                  <KeyRound className="h-6 w-6 text-[#F59E0B]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Activez votre espace Event Center</h1>
                  <p className="text-sm text-[#64748b]">Definissez votre mot de passe pour acceder a votre espace evenementiel</p>
                </div>
              </div>

              {accountInfo && (
                <div className="bg-[#fffbeb]/5 border border-[#F59E0B]/20 rounded-xl p-4 mb-6">
                  <p className="text-xs text-[#F59E0B] uppercase tracking-wider font-mono mb-1">Organisation</p>
                  <p className="text-white font-semibold" data-testid="activation-org-name">{accountInfo.org_name}</p>
                  <p className="text-sm text-[#94A3B8] mt-1" data-testid="activation-email">{accountInfo.email}</p>
                </div>
              )}

              {error && (
                <div data-testid="activation-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl mb-4 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Password */}
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1.5 block">Nouveau mot de passe</label>
                  <div className="relative">
                    <Input
                      data-testid="activation-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Choisissez un mot de passe"
                      className="bg-[#151B2B] border-[#2A3441] text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Password rules */}
                <div className="grid grid-cols-2 gap-1.5">
                  {PASSWORD_RULES.map((rule) => (
                    <div key={rule.label} className={`flex items-center gap-1.5 text-xs ${rule.test(password) ? "text-[#10B981]" : "text-[#4A5568]"}`}>
                      <CheckCircle2 className="h-3 w-3 shrink-0" />
                      {rule.label}
                    </div>
                  ))}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1.5 block">Confirmer le mot de passe</label>
                  <div className="relative">
                    <Input
                      data-testid="activation-confirm-password"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirmez votre mot de passe"
                      className="bg-[#151B2B] border-[#2A3441] text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-red-400 text-xs mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <Button
                  data-testid="activate-account-btn"
                  onClick={handleActivate}
                  disabled={!canSubmit}
                  className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#FBBF24] hover:to-[#F59E0B] text-white font-bold py-3 rounded-xl mt-2"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                  Activer mon espace Event Center
                </Button>
              </div>
            </>
          )}

          {/* SUCCESS */}
          {step === "success" && (
            <div className="text-center py-6" data-testid="activation-success">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F59E0B]/10 rounded-full mb-4">
                <CheckCircle2 className="h-8 w-8 text-[#F59E0B]" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Espace Event Center active !</h2>
              <p className="text-[#94A3B8] mb-6">Votre mot de passe a ete configure. Connectez-vous pour acceder a votre espace evenementiel GLOTECH Event Center.</p>
              <Button
                data-testid="go-to-login-btn"
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white font-bold py-3 rounded-xl"
              >
                Se connecter
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* EXPIRED */}
          {step === "expired" && (
            <div className="text-center py-6" data-testid="activation-expired">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-full mb-4">
                <AlertCircle className="h-8 w-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Lien expire</h2>
              <p className="text-[#94A3B8] mb-6">Ce lien d'activation a expire. Demandez un nouveau lien pour activer votre compte.</p>
              {!resendSent ? (
                <div className="space-y-3">
                  <Input
                    data-testid="resend-email-input"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Votre email"
                    className="bg-[#151B2B] border-[#2A3441] text-white"
                  />
                  <Button
                    data-testid="resend-activation-btn"
                    onClick={handleResend}
                    className="w-full bg-[#151B2B] hover:bg-[#1C2333] text-white border border-[#2A3441]"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Renvoyer le lien d'activation
                  </Button>
                </div>
              ) : (
                <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl p-4 text-sm text-[#10B981]">
                  Si un compte en attente existe, un nouveau lien a ete envoye.
                </div>
              )}
            </div>
          )}

          {/* INVALID */}
          {step === "invalid" && (
            <div className="text-center py-6" data-testid="activation-invalid">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-4">
                <AlertCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Lien invalide</h2>
              <p className="text-[#94A3B8] mb-6">Ce lien d'activation est invalide ou a deja ete utilise.</p>
              {!resendSent ? (
                <div className="space-y-3">
                  <Input
                    data-testid="resend-email-input-invalid"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Votre email"
                    className="bg-[#151B2B] border-[#2A3441] text-white"
                  />
                  <Button data-testid="resend-activation-btn-invalid" onClick={handleResend} className="w-full bg-[#151B2B] hover:bg-[#1C2333] text-white border border-[#2A3441]">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Renvoyer un lien d'activation
                  </Button>
                </div>
              ) : (
                <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl p-4 text-sm text-[#10B981]">
                  Si un compte en attente existe, un nouveau lien a ete envoye.
                </div>
              )}
              <Link to="/login" className="text-[#0099CC] text-sm hover:underline mt-4 inline-block">
                Retour a la connexion
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
